import { useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Tooltip } from "@/components/ui/overlays/Tooltip";

export type SideNavSubItem = {
  id: string;
  label: ReactNode;
  onSelect?: () => void;
};

export type SideNavItem = {
  id: string;
  label: ReactNode;
  /** Leading icon node. */
  icon?: ReactNode;
  active?: boolean;
  /** Trailing badge/slot (hidden when collapsed). */
  badge?: ReactNode;
  /** Hover flyout entries. When present, clicking the row does not call `onSelect`. */
  nested?: SideNavSubItem[];
  onSelect?: () => void;
};

export type SideNavSection = {
  id?: string;
  /** Uppercase section title (hidden when collapsed). */
  title?: ReactNode;
  items: SideNavItem[];
  /** Optional action row shown under the items (e.g. "Create new"). Hidden when collapsed. */
  action?: ReactNode;
};

export type SideNavProps = {
  sections: SideNavSection[];
  collapsed?: boolean;
  /** Expanded width in px. Defaults to 240. */
  width?: number;
  /** Collapsed width in px. Defaults to 56. */
  collapsedWidth?: number;
  /** Top slot, above the scrollable nav (e.g. a primary CTA). */
  header?: ReactNode;
  /** Extra content rendered inside the scroll area after the sections (e.g. a recents list). */
  children?: ReactNode;
  /** Bottom slot (e.g. user + settings). */
  footer?: ReactNode;
  className?: string;
  style?: CSSProperties;
};

function SideNavRow({ item, collapsed }: { item: SideNavItem; collapsed: boolean }) {
  const [flyoutTop, setFlyoutTop] = useState<number | null>(null);
  const hasNested = !!item.nested?.length;

  const open = (event: MouseEvent<HTMLButtonElement>) => {
    if (!hasNested || collapsed) return;
    setFlyoutTop(event.currentTarget.getBoundingClientRect().top);
  };

  const row = (
    <button
      type="button"
      onClick={() => (hasNested ? undefined : item.onSelect?.())}
      onMouseEnter={open}
      className={cn(
        "bfrs-mb-0.5 bfrs-flex bfrs-w-full bfrs-items-center bfrs-rounded-lg bfrs-outline-none bfrs-transition-colors",
        collapsed ? "bfrs-justify-center bfrs-gap-0 bfrs-px-0 bfrs-py-2.5" : "bfrs-h-8 bfrs-gap-2 bfrs-px-2.5",
        item.active ? "bfrs-bg-surface-app" : "hover:bfrs-bg-primary/[0.04]"
      )}
    >
      {item.icon && (
        <span className={cn("bfrs-flex bfrs-shrink-0 bfrs-items-center", item.active ? "bfrs-text-primary" : "bfrs-text-text-muted")}>
          {item.icon}
        </span>
      )}
      {!collapsed && (
        <>
          <span
            className={cn(
              "bfrs-flex-1 bfrs-truncate bfrs-text-left bfrs-text-[12px]",
              item.active ? "bfrs-font-normal bfrs-text-text-primary" : "bfrs-font-normal bfrs-text-text-secondary"
            )}
          >
            {item.label}
          </span>
          {item.badge}
          {hasNested && (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="bfrs-shrink-0 bfrs-text-text-muted">
              <path d="M4.5 3l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </>
      )}
    </button>
  );

  return (
    <div className="bfrs-relative" onMouseLeave={() => setFlyoutTop(null)}>
      {/* When collapsed the label is hidden, so surface it as a right-side tooltip on the whole row. */}
      {collapsed ? (
        <Tooltip content={item.label} side="right">
          {row}
        </Tooltip>
      ) : (
        row
      )}

      {hasNested && !collapsed && flyoutTop !== null && (
        <div
          className="bfrs-fixed bfrs-left-60 bfrs-z-[1000] bfrs-min-w-[200px] bfrs-overflow-hidden bfrs-rounded-xl bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-shadow-menu"
          style={{ top: flyoutTop }}
          onMouseEnter={() => setFlyoutTop(flyoutTop)}
          onMouseLeave={() => setFlyoutTop(null)}
        >
          <div className="bfrs-py-1">
            {item.nested!.map((sub) => (
              <button
                key={sub.id}
                type="button"
                onClick={() => { sub.onSelect?.(); setFlyoutTop(null); }}
                className="bfrs-flex bfrs-w-full bfrs-items-center bfrs-px-4 bfrs-py-2 bfrs-text-left bfrs-text-[13px] bfrs-text-text-secondary bfrs-transition-colors hover:bfrs-bg-surface-muted"
              >
                {sub.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible application sidebar navigation. Renders grouped sections of items
 * with active state, optional badges, and hover flyouts for nested items, plus
 * dedicated `header` and `footer` slots. All data (labels, icons, handlers) is
 * passed in — the component owns layout, collapse behavior, and chrome only.
 */
export function SideNav({
  sections,
  collapsed = false,
  width = 216,
  collapsedWidth = 56,
  header,
  children,
  footer,
  className,
  style
}: SideNavProps) {
  const resolvedWidth = collapsed ? collapsedWidth : width;

  return (
    <aside
      className={cn("bfrs-relative bfrs-flex bfrs-h-full bfrs-shrink-0 bfrs-flex-col bfrs-overflow-hidden bfrs-bg-surface-card", className)}
      style={{
        width: resolvedWidth,
        minWidth: resolvedWidth,
        borderRight: "1px solid hsl(var(--bfrs-color-border-strong))",
        transition: "width 0.22s cubic-bezier(0.4,0,0.2,1), min-width 0.22s cubic-bezier(0.4,0,0.2,1)",
        ...style
      }}
    >
      {header && <div className="bfrs-shrink-0 bfrs-px-2 bfrs-pb-1 bfrs-pt-3">{header}</div>}

      <div className={cn("bfrs-flex-1 bfrs-overflow-y-auto bfrs-pb-4 bfrs-pt-2 bfrs-[scrollbar-width:none]", collapsed ? "bfrs-px-2" : "bfrs-px-[15px]")}>
        {sections.map((section, index) => (
          <div
            key={section.id ?? index}
            className={cn(index > 0 && "bfrs-mt-2 bfrs-border-t bfrs-border-border-light bfrs-pt-2")}
          >
            {!collapsed && section.title && (
              <div className="bfrs-select-none bfrs-px-3 bfrs-pb-[3px] bfrs-pt-2.5 bfrs-text-[10px] bfrs-font-semibold bfrs-uppercase bfrs-tracking-[1.08px] bfrs-text-text-muted">
                {section.title}
              </div>
            )}
            {section.items.map((item) => (
              <SideNavRow key={item.id} item={item} collapsed={collapsed} />
            ))}
            {!collapsed && section.action && <div className="bfrs-mt-1">{section.action}</div>}
          </div>
        ))}
        {!collapsed && children}
      </div>

      {footer && <div className="bfrs-shrink-0 bfrs-border-t bfrs-border-border">{footer}</div>}
    </aside>
  );
}

SideNav.displayName = "SideNav";
